import {
  defalutModalContentProps,
  useModal,
} from '@components/Modal/ModalProvider';
import styles from './FileOperationModal.module.scss';
import { ReactComponent as Close } from 'img/btn-close.svg';
import Button from '@components/Button';
import useSeriesGroup from '@hooks/Series/useSeriesGroup';
import { useCallback, useState } from 'react';
import Radio from '@components/Radio';
import Modal from '@components/Modal';
import { useSeries } from '@hooks/Series/useSeries';
import { ReSegmentation } from '@api/jobqueues';
import useAuth from '@hooks/Auth/useAuth';
import useModalActions from '@hooks/common/useModalAction';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useAnalysisBaseUtilities } from 'utils/AnalysisBaseUtilities/useAnalysisBaseUtilities';
import { configStore } from 'config/configStore';

interface FileOperationModalContentProps extends defalutModalContentProps {
  studyId: string;
  mode: 'recalc' | 'delete' | 'download' | 're_ctffr' | 're_generate_data';
  handleClickRefresh: () => void,
}

function FileOperationModalContent({
  close,
  studyId,
  handleClickRefresh,
  mode,
}: FileOperationModalContentProps) {
  type SeriesOperationResult = {
    id: string;
    status?: number;
    message?: string;
    response?: {
      status?: number;
      data?: {
        message?: string;
      };
    };
  };

  const [selectedSeriesIds, setSelectedSeriesIds] = useState<string[]>([]);
  const { Series, isLoading, isError, isSuccess, refetch } = useSeriesGroup({
    study_id: studyId ?? '',
  });
  const { deleteSeriesDatas, recalcSeriesDatas, reCTFFRData, reGenerateData } = useSeries({
    id: '',
    getEnabled: false,
  });
  const { selectAction, errorAction } = useModalActions();
  const { successAction, errorDefaultAction } = useModalActions();
  const { exportSeriesData, exportSeriesLog } = useSeries({
    id: '',
    getEnabled: false,
  });

  const {
      selectedSeriesId: currentSeriesId,
      selectedSeries: currentSeries
    } = useBrowserPage();

  const { recalculateAnalysisListData, recalculateCTFFR } =
    useAnalysisBaseUtilities();

  const getResultStatus = (result: SeriesOperationResult) =>
    result.status ?? result.response?.status;

  const getFailedResults = (results: SeriesOperationResult[]) =>
    results.filter((result) => {
      const status = getResultStatus(result);
      return status !== 200 && status !== 201;
    });

  const getFailureReason = (result: SeriesOperationResult) => {
    const status = getResultStatus(result);
    return (
      result.response?.data?.message ??
      result.message ??
      (status ? `HTTP ${status}` : 'Unknown error')
    );
  };

  const handleDelete = async () => {
    if( !selectedSeriesIds || selectedSeriesIds.length === 0) return;
    await new Promise<void>((resolve, reject) => {
      selectAction('Are you sure you want to delete this series?', async () => {
        try {
          const results = await deleteSeriesDatas(selectedSeriesIds);
          resolve();
          close();
          const failIds = results
            .filter((r) => r.status !== undefined && r.status !== 200 && r.status !== 201 && r.status !== 204)
            .map((r) => r.id);

          let msg = 'Deletion ';
          if(failIds.length === 0) msg += 'Completed'
          else{
            msg += 'Failed for following IDs:\n';
            msg += failIds.map((id) => `- ${id}`).join('\n');
          }
          await successAction(msg, handleClickRefresh);
        } catch (err) {
          reject(err);
        }
      });
    });
  };

  const handleReCalculateSegmentation = async () => {
    if (!selectedSeriesIds || selectedSeriesIds.length === 0) return;
    try {
      const results = await recalcSeriesDatas(selectedSeriesIds);
      close();

      const failedResults = getFailedResults(results);
      let msg = 'Recalculation ';
      if (failedResults.length === 0) msg += 'Completed';
      else {
        msg += 'Failed for following IDs:\n';
        msg += failedResults
          .map((result) => `- ${result.id}: ${getFailureReason(result)}`)
          .join('\n');
      }
      if (currentSeriesId && currentSeries) {
        if (selectedSeriesIds.includes(currentSeriesId)) {
          recalculateAnalysisListData();
        }
      }
      if (failedResults.length === 0) {
        await successAction(msg, handleClickRefresh);
        return;
      }
      await errorDefaultAction(msg, handleClickRefresh);
    } catch {
      return;
    }
  };
  const runReCalculateCTFFR = async () => {
    try {
      const results = await reCTFFRData(selectedSeriesIds);
      configStore.setData({shouldRunFFR:false})
      close();

      const failedResults = getFailedResults(results);
      let msg = 'Processing CT-FFR ';
      if (failedResults.length === 0) msg += 'Completed';
      else {
        msg += 'Failed for following IDs:\n';
        msg += failedResults
          .map((result) => `- ${result.id}: ${getFailureReason(result)}`)
          .join('\n');
      }
      if (currentSeriesId && currentSeries) {
        if (selectedSeriesIds.includes(currentSeriesId)) {
          recalculateCTFFR();
        }
      }
      if (failedResults.length === 0) {
        await successAction(msg, handleClickRefresh);
        return;
      }
      await errorDefaultAction(msg, handleClickRefresh);
    } catch {
      return;
    }
  };

  const handleReCalculateCTFFR = async () => {
    if (!selectedSeriesIds || selectedSeriesIds.length === 0) return;

    await selectAction(
      'Do you want to run CT-FFR?',
      runReCalculateCTFFR,
      { left: 'No', right: 'Yes' },
      'ctffr-confirm-modal',
    );
  };
  const handleReGenerateData = async () => {
    if (!selectedSeriesIds || selectedSeriesIds.length === 0) return;
    try {
      const results = await reGenerateData(selectedSeriesIds);
      close();

      const failedResults = getFailedResults(results);
      let msg = 'Generating Data ';
      if (failedResults.length === 0) msg += 'Completed';
      else {
        msg += 'Failed for following IDs:\n';
        msg += failedResults
          .map((result) => `- ${result.id}: ${getFailureReason(result)}`)
          .join('\n');
      }
      if (failedResults.length === 0) {
        await successAction(msg, handleClickRefresh);
        return;
      }
      await errorDefaultAction(msg, handleClickRefresh);
    } catch {
      return;
    }
  };

  const handleSetSeriesId = () => {
    if( !selectedSeriesIds || selectedSeriesIds.length === 0) return;
    // mode가 download selectedSeriesIds 하나만. SeriesId 다중선택 아님.
    exportSeriesData(selectedSeriesIds[0]);
  };
  const handleSetSeriesIdLog = () => {
    if( !selectedSeriesIds || selectedSeriesIds.length === 0) return;
    // mode가 download selectedSeriesIds 하나만. SeriesId 다중선택 아님.
    exportSeriesLog(selectedSeriesIds[0]);
  };

  return (
    <div className={styles['modal-container']}>
      <div className={styles['modal-container-1']}>
        <Close
          className={styles['modal-container-icon']}
          onClick={close}
        />
      </div>
      <div className={styles['modal-container-2']}>
        {mode === 'recalc'
          ? 'Select a series to ReCalculate'
          : mode === 'download'
            ? 'Select a series to Download'
            : mode === 're_ctffr'
              ? 'Select a series to Process CT-FFR'
              : mode === 're_generate_data'
                ? 'Select a series to Generate Data'
            : 'Select a series to Delete'}
      </div>
      <div className={styles['modal-container-3']}>
        {isSuccess && Series.data.length > 0 && (
          <ul className={styles.seriesList}>
            {Series.data.map((item, index) => (
              <li key={item.id} className={styles.seriesItem}>
                <label className={styles.seriesLabel}>
                  <Radio
                    onChange={() =>
                      setSelectedSeriesIds((prev) => {
                        if (mode === 'download') return [item.id];
                        return prev.includes(item.id)
                          ? prev.filter((id) => id !== item.id)
                          : [...prev, item.id];
                      })
                    }
                    checked={selectedSeriesIds.includes(item.id)}
                    value={`option${index}`}
                  />
                  <div className={styles.seriesInfo}>
                    <span className={styles.seriesId}>
                      UUID : {item.seriesInstanceUid}
                    </span>
                    <span className={styles.seriesDesc}>
                      Description : {item.seriesDescription || '설명 없음'}
                    </span>
                    <span className={styles.seriesDesc}>
                      KVP : {item.imageTag.kvp || '설명 없음'}
                    </span>
                    <span className={styles.seriesDesc}>
                      Images : {item.imageTag.depthInterpolated || '설명 없음'}
                    </span>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={styles['modal-container-4']}>
        <Button color="dark" onClick={() => close()}>
          Close
        </Button>
        {mode === 'delete' && (
          <Button disabled={selectedSeriesIds.length === 0} onClick={handleDelete}>
            Delete
          </Button>
        )}
        {mode === 'recalc' && (
          <Button disabled={selectedSeriesIds.length === 0} onClick={handleReCalculateSegmentation}>
            ReCalculate
          </Button>
        )}
        {mode === 're_ctffr' && (
          <Button disabled={selectedSeriesIds.length === 0} onClick={handleReCalculateCTFFR}>
            Run
          </Button>
        )}
        {mode === 're_generate_data' && (
          <Button disabled={selectedSeriesIds.length === 0} onClick={handleReGenerateData}>
            Generate Data
          </Button>
        )}
        {mode === 'download' && (
           <Button disabled={selectedSeriesIds.length === 0} onClick={handleSetSeriesId}>
             Data
           </Button>
         )}
         {mode === 'download' && (
           <Button disabled={selectedSeriesIds.length === 0} onClick={handleSetSeriesIdLog}>
             Log
           </Button>
         )}
      </div>
    </div>
  );
}

export default function useFileOperationModal() {
  const { openModal, closeModal } = useModal();

  const handleOpenFileOperationModal = useCallback((
    studyId: string,
    handleClickRefresh: () => void,
    mode: 'recalc' | 'delete' | 'download' | 're_ctffr' | 're_generate_data',
  ) => {
      const id = openModal(
        <Modal
          type="page"
          close={() => closeModal(id)}
          description={
            <FileOperationModalContent
              close={() => closeModal(id)}
              studyId={studyId}
              mode={mode}
              handleClickRefresh = {handleClickRefresh}
            />
          }
        />,
      );
    },
    [openModal],
  );

  return { handleOpenFileOperationModal };
}
